import { useCallback, useState } from 'react';
import { Upload, FileJson, X } from 'lucide-react';

interface FileDropzoneProps {
  label: string;
  accept?: string;
  maxSizeBytes?: number;
  helperText?: string;
  onFileSelected: (file: File | null) => void;
}

const DEFAULT_MAX_SIZE = 1_048_576; // 1 MB

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export function FileDropzone({
  label,
  accept = '.json',
  maxSizeBytes = DEFAULT_MAX_SIZE,
  helperText,
  onFileSelected
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const validateFile = (file: File) => {
    if (accept && !file.name.toLowerCase().endsWith(accept)) {
      return `File must be a ${accept} file`;
    }
    if (file.size > maxSizeBytes) {
      return `File size exceeds ${formatBytes(maxSizeBytes)}`;
    }
    return null;
  };

  const handleFile = (file: File | null) => {
    if (!file) return;
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      onFileSelected(null);
      return;
    }
    setError(null);
    setSelectedFile(file);
    onFileSelected(file);
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError(null);
    onFileSelected(null);
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <div
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        className={`rounded-lg border-2 border-dashed p-5 transition ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
            <Upload className="h-5 w-5 text-blue-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              Drag and drop your snapshot JSON, or
              <label className="text-blue-600 hover:text-blue-500 font-semibold ml-1 cursor-pointer">
                browse
                <input
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            </p>
            <p className="text-xs text-gray-500">
              {helperText || `JSON only • Max ${formatBytes(maxSizeBytes)}`}
            </p>
          </div>
        </div>
      </div>

      {selectedFile && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
          <div className="flex items-center gap-2">
            <FileJson className="h-4 w-4 text-slate-500" />
            <div className="text-sm text-slate-700">
              <span className="font-medium">{selectedFile.name}</span>
              <span className="text-slate-400 ml-2">{formatBytes(selectedFile.size)}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={clearFile}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
}
